import React, { useState, useEffect } from "react";
import axios from "axios";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import {
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField,
  Divider,
  Paper,
} from "@mui/material";
import { fetchThingDetails } from "features/thing/thingThunk";
import { useDispatch, useSelector } from "react-redux";
import { fetchJourneyData } from "features/routeReplay/routeReplayThunk";
import { useMaterialUIController } from "context";

function SendNotification() {
  const [broadcast, setBroadcast] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [message, setMessage] = useState("");
  const [snackbar, setSnackbar] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const dispatch = useDispatch();
  const things = useSelector((state) => state.things.filterThing || []);
  const journey = useSelector((state) => state.routeReplay.journey || []);

  // Fetch journeys when vehicle selected
  useEffect(() => {
    if (selectedVehicle) {
      dispatch(fetchJourneyData(selectedVehicle.value));
    }
  }, [selectedVehicle, dispatch]);

  // Fetch vehicle list
  useEffect(() => {
    dispatch(fetchThingDetails());
  }, [dispatch]);

  const handleSend = async () => {
    if (!message || (!broadcast && (!selectedVehicle || !selectedJourney))) {
      return setSnackbar({
        color: "error",
        icon: "warning",
        title: "Error",
        content: "Please fill all required fields!",
        open: true,
      });
    }

    try {
      const token = localStorage.getItem("token");

      let response;

      if (broadcast) {
        response = await axios.post(
          "https://v5.dhundhoo.com/message/broadcast",
          { message },
          {
            headers: {
              "x-dhundhoo-session": token,
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        const payload = {
          eventType: "broadcast",
          name: "TO_SUBSCRIBERS_OF_JOURNEY",
          data: {
            journeyId: selectedJourney.value,
            message: message,
            thingId: selectedVehicle.value,
          },
        };

        response = await axios.post(
          "https://v5.dhundhoo.com/message/send",
          payload,
          {
            headers: {
              "x-dhundhoo-session": token,
              "Content-Type": "application/json",
            },
          }
        );
      }

      console.log("API Response:", response.data);

      setSnackbar({
        color: "success",
        icon: "check",
        title: "Success",
        content: broadcast
          ? "Broadcast sent successfully!"
          : `Notification sent to ${selectedVehicle.label} for ${selectedJourney.label}`,
        open: true,
      });

      setMessage("");
      setSelectedVehicle(null);
      setSelectedJourney(null);
    } catch (error) {
      console.error("Send Notification Error:", error);
      setSnackbar({
        color: "error",
        icon: "warning",
        title: "Error",
        content: error.response?.data?.message || "Failed to send notification",
        open: true,
      });
    }
  };

  const handleClear = () => {
    setMessage("");
    setSelectedVehicle(null);
    setSelectedJourney(null);
  };

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      minHeight="90vh"
      bgcolor="#f5f7fa"
      p={4}
    >
      <Paper
        elevation={6}
        style={{
          borderRadius: "16px",
          padding: "32px",
          width: "100%",
          maxWidth: "650px",
          backgroundColor: darkMode ? "#344767" : "#fff",
        }}
      >
        {/* Title */}
        <MDTypography
          variant="h5"
          fontWeight="bold"
          textAlign="center"
          gutterBottom
          color={darkMode ? "white" : undefined}


        >
          Send Notification
        </MDTypography>

        {/* Toggle */}
        <MDBox display="flex" justifyContent="center" mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={broadcast}
                onChange={(e) => setBroadcast(e.target.checked)}
                color="info"
              />
            }
            label={
              <MDTypography variant="button" fontWeight="medium">
                Broadcast Message
              </MDTypography>
            }
          />
        </MDBox>
        <Divider sx={{ mb: 3 }} />

        {/* Vehicle Dropdown */}
        {!broadcast && (
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" gutterBottom>
              <span style={{ color: "red" }}>*</span> Vehicle Number
            </MDTypography>
            {/* <Autocomplete
              options={things.map((thing) => ({
                label: thing.vehicleNumber || thing.name,
                value: thing.id,
              }))}
              value={selectedVehicle}
              onChange={(event, newValue) => {
                setSelectedVehicle(newValue);
                setSelectedJourney(null);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select vehicle" size="small" />
              )}
            /> */}
            <Autocomplete
              options={things.map((thing) => ({
                label: `${thing.vehicleNumber || thing.name}${thing.nickName ? ` - ${thing.nickName}` : ""
                  }`,
                value: thing.id,
              }))}
              value={selectedVehicle}
              onChange={(event, newValue) => {
                setSelectedVehicle(newValue);
                setSelectedJourney(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select vehicle"
                  size="small"
                />
              )}
            />
          </MDBox>
        )}

        {/* Journey Dropdown */}
        {!broadcast && selectedVehicle && (
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" gutterBottom>
              Select Journey
            </MDTypography>
            <Autocomplete
              options={journey.map((j) => ({
                label: j.selector,
                value: j.journeyId,
              }))}
              value={selectedJourney}
              onChange={(event, newValue) => setSelectedJourney(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select journey" size="small" />
              )}
            />
          </MDBox>
        )}

        {/* Message */}
        <MDBox mb={3}>
          <MDTypography variant="button" fontWeight="medium" gutterBottom>
            <span style={{ color: "red" }}>*</span> Message
          </MDTypography>
          <MDInput
            fullWidth
            placeholder="Enter the message here..."
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ borderRadius: "12px" }}
          />
        </MDBox>

        {/* Buttons */}
        <MDBox display="flex" justifyContent="center" gap={2} mt={2}>
          <MDButton
            variant="contained"
            color="warning"
            size="medium"
            onClick={handleSend}
          >
            {broadcast ? "Send Broadcast" : "Send Notification"}
          </MDButton>
          <MDButton
            variant="outlined"
            color="secondary"
            size="medium"
            onClick={handleClear}
          >
            Clear
          </MDButton>
        </MDBox>
      </Paper>

      {/* Snackbar */}
      {snackbar && (
        <MDSnackbar
          color={snackbar.color}
          icon={snackbar.icon}
          title={snackbar.title}
          content={snackbar.content}
          open={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          close
        />
      )}
    </MDBox>
  );
}

export default SendNotification;